
import type { LoanTypeOption } from './types';
import { 
  isValidNumber, 
  safeNumberConversion, 
  calculateEffectiveSellerCredit 
} from './transactionSafeCalculations';

export interface SellerCreditLimits {
  maxPercentage: number;
  description: string;
  dependsOnDownPayment?: boolean;
}

export interface SellerCreditValidationResult {
  isValid: boolean;
  isWarning: boolean;
  maxAllowed: number;
  message: string;
  effectiveCredit: number;
}

/**
 * Get seller credit limits based on loan type and down payment
 */
export const getSellerCreditLimits = (
  loanType: LoanTypeOption,
  downPaymentPercentage: number
): SellerCreditLimits => {
  const safeDownPaymentPercentage = safeNumberConversion(downPaymentPercentage, 0);
  
  switch (loanType) {
    case 'conventional':
      // Conventional loans have tiered limits based on down payment
      if (safeDownPaymentPercentage >= 25) {
        return {
          maxPercentage: 9,
          description: 'Up to 9% with 25%+ down payment',
          dependsOnDownPayment: true
        };
      } else if (safeDownPaymentPercentage >= 10) {
        return {
          maxPercentage: 6,
          description: 'Up to 6% with 10-24% down payment',
          dependsOnDownPayment: true
        };
      } else {
        return {
          maxPercentage: 3,
          description: 'Up to 3% with less than 10% down payment',
          dependsOnDownPayment: true
        };
      }
    
    case 'fha':
      return {
        maxPercentage: 6,
        description: 'Up to 6% for FHA loans'
      };
    
    case 'va':
      return {
        maxPercentage: 4,
        description: 'Up to 4% for VA loans'
      };
    
    case 'usda':
      return {
        maxPercentage: 6,
        description: 'Up to 6% for USDA loans'
      };
    
    default:
      return {
        maxPercentage: 3,
        description: 'Up to 3% (default limit)'
      };
  }
};

/**
 * Validate seller credit percentage and calculate effective amount with transaction safety
 */
export const validateSellerCredit = (
  sellerCreditPercentage: number,
  homePrice: number,
  totalClosingCosts: number,
  loanType: LoanTypeOption,
  downPaymentPercentage: number
): SellerCreditValidationResult => {
  // Transaction-safe input validation
  const safeSellerCreditPercentage = safeNumberConversion(sellerCreditPercentage, 0);
  const safeHomePrice = safeNumberConversion(homePrice, 0);
  const safeTotalClosingCosts = safeNumberConversion(totalClosingCosts, 0);
  const safeDownPaymentPercentage = safeNumberConversion(downPaymentPercentage, 0);
  
  // Validate all inputs are valid numbers
  if (!isValidNumber(safeSellerCreditPercentage) || 
      !isValidNumber(safeHomePrice) || 
      !isValidNumber(safeTotalClosingCosts) || 
      !isValidNumber(safeDownPaymentPercentage)) {
    return {
      isValid: false,
      isWarning: false,
      maxAllowed: 0,
      message: 'Invalid input values detected',
      effectiveCredit: 0
    };
  }
  
  const limits = getSellerCreditLimits(loanType, safeDownPaymentPercentage);
  const maxAllowed = limits.maxPercentage;
  const requestedCreditAmount = (safeSellerCreditPercentage / 100) * safeHomePrice;
  
  // Calculate effective credit using transaction-safe function
  const effectiveCredit = calculateEffectiveSellerCredit(requestedCreditAmount, safeTotalClosingCosts);
  
  // Validation logic with transaction safety
  if (safeSellerCreditPercentage === 0) {
    return {
      isValid: true,
      isWarning: false,
      maxAllowed,
      message: '',
      effectiveCredit: 0
    };
  }
  
  if (safeSellerCreditPercentage > maxAllowed) {
    return {
      isValid: false,
      isWarning: false,
      maxAllowed,
      message: `Exceeds ${loanType.toUpperCase()} limit of ${maxAllowed}%. ${limits.description}`,
      effectiveCredit
    };
  }
  
  if (requestedCreditAmount > safeTotalClosingCosts) {
    return {
      isValid: true,
      isWarning: true,
      maxAllowed,
      message: `Credit exceeds closing costs. Only $${effectiveCredit.toLocaleString()} will be applied.`,
      effectiveCredit
    };
  }
  
  if (safeSellerCreditPercentage > maxAllowed * 0.8) {
    return {
      isValid: true,
      isWarning: true,
      maxAllowed,
      message: `Approaching ${loanType.toUpperCase()} limit of ${maxAllowed}%`,
      effectiveCredit
    };
  }
  
  return {
    isValid: true,
    isWarning: false,
    maxAllowed,
    message: '',
    effectiveCredit
  };
};
